import { ParticipantView, QuestionView } from '@views';
import { useSaveDrawerSectionAction } from '@actions';
import { useEffect, useState } from 'react';
import { useNextSurveySectionViewCreator } from '@app/view-creator/next-survey-section-view-creator';
import { nextWorkingSection, WorkingSection } from '@components/SurveyDrawer/workingSection';
import { EmptyFunction } from '@utils/common-types';
import { useAllCohortUsersViewCreator } from '@viewCreators/active-participants-view-creator';
import { useNavigationEligibility } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { isAskedSurveys } from '@components/SurveyDrawer/drawerEligibility';

export type SurveyDrawerController = {
  surveyTitle: string;
  isDrawerOpen: boolean;
  /**
   * True once every query the drawer's visibility depends on has resolved. Until then the
   * dashboard should keep showing its loader rather than let the drawer flash open/closed
   * as the survey section and cohort-user (role) queries settle independently.
   */
  isReady: boolean;
  activeStep: number;
  currentQuestion: QuestionView;
  questions: QuestionView[];
  handleNext: EmptyFunction;
  handlePrev: EmptyFunction;
  saveResponses: () => void;
  isSaving: boolean;
  refresh: () => Promise<any>;
  activeParticipants: ParticipantView[];
};

export const useSurveyDrawerController = (groupId: number): SurveyDrawerController => {
  const { data, refetch, isLoading: isSectionLoading } = useNextSurveySectionViewCreator(groupId);
  const [working, setWorking] = useState<WorkingSection | null>(null);
  const workingSection = working?.section ?? null;
  const [activeStep, setActiveStep] = useState<number>(0);
  const { mutateAsync: saveDrawerSection, isPending: isSaving } = useSaveDrawerSectionAction();
  const [currentQuestion, setCurrentQuestion] = useState<QuestionView>(null as unknown as QuestionView);
  const [questions, setQuestions] = useState<QuestionView[]>([]);
  const [surveyTitle, setSurveyTitle] = useState<string>('');
  const { data: userData, isLoading: isUserDataLoading } = useAllCohortUsersViewCreator(groupId);
  const { setSectionRef, setCurrentStep, triggerEligibility } = useNavigationEligibility();
  const cookies = useAppCookies();
  const currentUserId = cookies.profile.id;
  const currentUser = userData?.allActiveCohortUsers.find((p) => p.CohortUser.userId === currentUserId);

  useEffect(() => {
    if (!data) return;
    setWorking((current) => nextWorkingSection(current, data));
  }, [data]);

  useEffect(() => {
    if (!workingSection) return;
    setSectionRef(workingSection);
    setSurveyTitle(workingSection.name);

    const allQuestions = workingSection.questions ?? [];
    setQuestions(allQuestions);

    const step = Math.min(activeStep, allQuestions.length - 1);
    setCurrentQuestion(allQuestions[step]);
    triggerEligibility();
  }, [workingSection, activeStep]);

  // Everything the drawer's visibility depends on: the survey section itself and the cohort
  // users (which tell us the current user's role). Until both have resolved we can't know
  // whether to show the drawer, so we report "not ready" and stay closed. Reporting this up
  // lets the dashboard hold its loader instead of letting the drawer flash open then closed
  // once the role query lands.
  const isReady = !isSectionLoading && !isUserDataLoading;

  // Show the drawer if:
  //   Everything it depends on has loaded
  //   The user is a participant in this cohort, whatever other roles they have
  //   The survey has at least one section
  //   That section has questions
  const isDrawerOpen =
    isReady && isAskedSurveys(currentUser?.CohortUser.role) && !!workingSection && workingSection.questions.length > 0;

  const handleNext = () => {
    setActiveStep((prev) => {
      const next = prev + 1;
      setCurrentStep(next);
      return next;
    });
  };

  const handlePrev = () => {
    setActiveStep((prev) => {
      const next = prev - 1;
      setCurrentStep(next);
      return next;
    });
  };

  const handleSaveResponses = async () => {
    if (isSaving) return;
    if (data && workingSection) {
      await saveDrawerSection({ view: data, section: workingSection, groupId });
    }
    setActiveStep(0);
    // The eligibility provider tracks the step separately and only resets itself when the
    // section id changes; without this it stays pointed at the last question of the section
    // just finished, which leaves Finish visible over the next section's first question.
    setCurrentStep(0);
    await refetch();
  };
  const activeParticipants = userData?.activeParticipants ?? [];
  return {
    isDrawerOpen,
    isReady,
    questions,
    activeStep,
    surveyTitle,
    currentQuestion,
    handleNext,
    handlePrev,
    saveResponses: handleSaveResponses,
    isSaving,
    refresh: refetch,
    activeParticipants,
  };
};
